<template>
  <q-dialog v-model="showDialogRef" @hide="emitClose">
    <q-card class="q-pa-md bg-black text-white col-6 dialog-bordered" style="max-height: 80vh;">
      <!-- Search Input inside the dialog -->
      <q-card-section>
        <q-input
          v-model="dialogSearch"
          label="Search"
          dark
          outlined
          dense
          class="col-12"
          @update:model-value="performSearch"
        >
          <template v-slot:append>
            <q-icon v-if="dialogSearch !== ''" name="close" @click="clearSearch" class="cursor-pointer" />
            <q-icon name="search" />
          </template>
        </q-input>
      </q-card-section>

      <!-- Show search results -->
      <q-card-section class="scrollable-section" v-if="hasResults">
        <q-list bordered>
          <q-item v-for="(result, index) in results" :key="index" clickable v-ripple>
            <q-item-section>
              <!-- Display the title of the result -->
              <NuxtLink :to="result.id" class="text-bold scifi-font">{{ result.title }}</NuxtLink>
              <!-- Display a snippet of the content (first 50 characters) -->
              <p class="content-snippet">{{ getSnippet(result.content) }}</p>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>

      <!-- Fallback if no results are found -->
      <q-card-section v-else>
        <div>No results found.</div>
      </q-card-section>

      <!-- Dialog actions, always visible -->
      <q-card-actions align="right" class="sticky-actions">
        <q-btn flat label="Close" @click="closeDialog" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import MiniSearch, { type SearchResult as MiniSearchResult } from 'minisearch';

// Extend the existing SearchResult type from MiniSearch
interface ExtendedSearchResult extends MiniSearchResult {
  content: string;           // Adding content field
  titles: string[];          // Adding titles field
  match: {                   // Modifying match to include the matched fields
    [key: string]: string[];
  };
}

const results = ref<ExtendedSearchResult[]>([]);

// v3: searchContent() (a v2-only composable) is gone. Build a MiniSearch index
// from the content search sections (queryCollectionSearchSections splits docs
// into heading-anchored sections whose `id` is a `/path#anchor` link).
const { data: sections } = await useAsyncData('search-sections', () =>
  queryCollectionSearchSections('content')
);
const miniSearch = computed(() => {
  const ms = new MiniSearch({
    fields: ['title', 'titles', 'content'],
    storeFields: ['title', 'titles', 'content', 'id'],
    searchOptions: { prefix: true, fuzzy: 0.2 },
  });
  if (sections.value) ms.addAll(sections.value as any[]);
  return ms;
});

// Define emits to allow the component to emit events
const emit = defineEmits(['close']);

// Emit close event when dialog is hidden
const emitClose = () => {
  emit('close');
};


// Props passed from the parent
const props = defineProps({
  showDialog: Boolean,
  initialSearch: String,
  searchFunction: Function
});

const dialogSearch = ref(props.initialSearch || '');
const showDialogRef = ref(props.showDialog);
// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Watch for dialog open state to reset search
watch(() => props.showDialog, (newVal) => {
  if (newVal) {
    dialogSearch.value = props.initialSearch || "";
    performSearch();
  }
  showDialogRef.value = newVal;
});

// Computed property to check if results exist
const hasResults = computed(() => results.value.length > 0);

// Debounced search function with a 1.5 second delay
const performSearch = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);  // Clear the previous timer
  }
  
  debounceTimer = setTimeout(async () => {
    if (dialogSearch.value.length > 2) {
      const found = miniSearch.value.search(dialogSearch.value);
      results.value = found.map((result: MiniSearchResult) => {
        return {
          ...result,
          content: (result as any).content || '',
          titles: (result as any).titles || [],
          match: result.match || {}
        } as ExtendedSearchResult;
      });
      showDialogRef.value = true; // Show the dialog when we have results
    } else {
      results.value = [];
      showDialogRef.value = false; // Hide the dialog if the search query is too short
    }
  }, 1000);  // Set debounce delay to 1 seconds
};

// Watch for changes in the search input and trigger the debounced search
watch(dialogSearch, performSearch);

// Clear the search input and reset results
function clearSearch() {
  dialogSearch.value = '';
  results.value = [];
}

// Function to get a snippet of the content
function getSnippet(content: string): string {
  const snippetLength = 100;
  return content.length > snippetLength ? content.slice(0, snippetLength) + '...' : content;
}

// Close the dialog and emit the close event
const closeDialog = () => {
  showDialogRef.value = false;
  emitClose();
};
</script>

<style scoped>
/* Dark card background and text styling */
.q-card {
  background-color: #000000; /* Dark background */
  color: white; /* White text */
}

/* Styling for the content snippet */
.content-snippet {
  color: #ddd;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

/* Make the q-input text white */
.q-input input {
  color: white;
}

/* Make the search results scrollable */
.scrollable-section {
  max-height: 50vh; /* Adjust based on your needs */
  overflow-y: auto; /* Scroll when content exceeds max height */
}

/* Keep the actions (close button) sticky at the bottom */
.sticky-actions {
  position: sticky;
  bottom: 0;
  background-color: inherit; /* Keep background consistent with the rest of the card */
}

/* Add a green border to the dialog */
.dialog-bordered {
  border: 2px solid #00ff00; /* Green border */
  box-shadow: 0 0 4px #00ff00; /* Optional: green glow effect */
}
</style>
